import {
  getMyCoverage,
  getCommunesByRegion,
  getRegions as getRegionsWithIds,
} from "../api/settings";
import { getRegions } from "../api/contracts";
import { buildCommuneIndex, normalizeCommune } from "./communes";
import { apiBodyMessage, apiErrorMessage } from "./apiErrorMessage";

/**
 * `getRegions` no lanza: ante un fallo devuelve `{ error: <cuerpo> }`. Ese
 * fallo se relanza con este error para que `resolveCoverage` lo trate como
 * `'api-error'` (reintentable, con el motivo del backend) y no como
 * `'unresolvable'`, que culparía a la configuración del operador.
 */
class RegionsCatalogError extends Error {
  constructor(body) {
    super("contracts/countries/regions failed");
    this.name = "RegionsCatalogError";
    this.body = body;
  }
}

/**
 * Mapa comuna -> nombre de su región, para todo el país.
 *
 * `contracts/countries/regions` devuelve las regiones con sus comunas
 * ANIDADAS, así que sale de una sola request y sin tener que matchear
 * ObjectIds de región contra el catálogo de comunas.
 *
 * La clave es `normalizeCommune`, la misma que usa `buildCommuneIndex`: sin
 * normalizar, "Ñuñoa" y "NUNOA" serían comunas distintas.
 */
async function buildRegionByCommune(countryId) {
  if (!countryId) return null;
  const regions = await getRegions({ countryId });
  // Cualquier cosa que no sea un array es un fallo del endpoint, no un
  // catálogo sin datos.
  if (!Array.isArray(regions)) throw new RegionsCatalogError(regions?.error);

  const map = new Map();
  for (const region of regions) {
    const regionName = region?.name;
    if (!regionName) continue;
    for (const commune of region?.communes ?? []) {
      const key = normalizeCommune(commune?.name);
      if (key) map.set(key, regionName);
    }
  }
  return map.size > 0 ? map : null;
}

/**
 * Cobertura agrupada por `code` de región, para los desplegables del pedido a
 * mano (que identifican la región por su `code`, no por ObjectId).
 *
 * Por qué se cruza por id y no por nombre de comuna: en Argentina los nombres
 * se repiten entre provincias (San Martín, Junín, Rivadavia…), así que
 * `regionByCommune` podría marcar como cubierta una provincia que no lo está.
 * `contracts/countries/regions` no expone el `_id` de la región (el schema lo
 * declara `_id: false`); `regiones.json` del BFF sí, y es la misma colección.
 *
 * Es un extra de UI y por eso no tumba la cobertura: si falla devuelve `null`
 * y la carga por Excel sigue igual. Con `null` el formulario muestra el
 * catálogo completo, como antes (decisión de producto, 2026-10-20).
 *
 * @param {string} countryId
 * @param {Array<{regionId: string, full: boolean, names: string[]}>} perRegion
 * @returns {Promise<Map<string, {full: boolean, communes: Set<string>}> | null>}
 *   `communes` va normalizado con `normalizeCommune`.
 */
async function buildCoverageByRegionCode(countryId, perRegion) {
  try {
    const resp = await getRegionsWithIds(countryId);
    const body = resp?.data?.data ?? resp?.data;
    if (!Array.isArray(body)) return null;

    const codeById = new Map(
      body
        .filter((region) => region?._id && region?.code)
        .map((region) => [String(region._id), region.code]),
    );

    const byRegionCode = new Map();
    for (const area of perRegion) {
      const code = codeById.get(area.regionId);
      if (!code) continue;
      // Unión, igual que `names`: `my-coverage` ya fusiona por región, pero si
      // una región llegara repetida no se puede perder la primera.
      const entry = byRegionCode.get(code) ?? { full: false, communes: new Set() };
      entry.full = entry.full || area.full;
      buildCommuneIndex(area.names).forEach((name) => entry.communes.add(name));
      byRegionCode.set(code, entry);
    }
    return byRegionCode.size > 0 ? byRegionCode : null;
  } catch (err) {
    console.warn("coverageCommunes: no se pudo agrupar la cobertura por región", err?.message);
    return null;
  }
}

/** Cobertura por región utilizable, o `null` si hay que mostrar el catálogo entero. */
function usableByRegionCode(coverage) {
  const byRegionCode = coverage?.byRegionCode;
  return byRegionCode instanceof Map && byRegionCode.size > 0 ? byRegionCode : null;
}

/**
 * Regiones del desplegable que el operador cubre.
 *
 * Sin cobertura determinable devuelve la lista tal cual. Lo mismo si el cruce
 * deja la lista vacía: eso es un dato inconsistente (cobertura de otro país,
 * catálogo cambiado), no "no se entrega en ningún lado", y dejar el
 * desplegable vacío frenaría la carga del pedido.
 *
 * @param {Array<{value: string, label: string}>} regions `value` = code.
 * @param {object|null} coverage resultado de `resolveCoverage`.
 */
export function filterRegionsByCoverage(regions, coverage) {
  const byRegionCode = usableByRegionCode(coverage);
  if (!byRegionCode) return regions;
  const covered = regions.filter((region) => byRegionCode.has(region.value));
  return covered.length > 0 ? covered : regions;
}

/**
 * Comunas de `regionCode` que el operador cubre. Región completa (`full`) o
 * región fuera de la cobertura conocida → lista tal cual; mismo criterio de
 * "vacío = inconsistente" que `filterRegionsByCoverage`.
 *
 * @param {Array<{value: string, label: string}>} communes `value` = nombre.
 * @param {string} regionCode
 * @param {object|null} coverage resultado de `resolveCoverage`.
 */
export function filterCommunesByCoverage(communes, regionCode, coverage) {
  const area = usableByRegionCode(coverage)?.get(regionCode);
  if (!area || area.full) return communes;
  const covered = communes.filter((commune) =>
    area.communes.has(normalizeCommune(commune.value)),
  );
  return covered.length > 0 ? covered : communes;
}

/**
 * Comunas que el/los operador(es) de este comercio realmente cubren.
 *
 * Se arma con dos endpoints que YA existen y que la vista de reglas de
 * integración (`OrderRulesDrawer`) usa exactamente igual:
 *   1. `GET contracts/my-coverage` → { operators[], coverage:[{regionId, full, communeIds}] }
 *      El companyId sale del JWT, así que no hay forma de pedir la cobertura de
 *      otro comercio.
 *   2. `GET comunas_by_region.json?region_id=` → { communes:[{_id, name}] }
 *      La cobertura viaja con ObjectIds; los nombres salen de acá.
 *
 * Dos decisiones que no son obvias:
 *
 * - `full: true` significa "toda la región", sin enumerar comunas. Hay que
 *   expandirlo con el catálogo o el comercio vería una cobertura vacía justo
 *   donde es total.
 *
 * - Un comercio puede tener contrato con VARIOS operadores (`operators[]` es
 *   plural). Se toma la UNIÓN: una comuna que cubre uno solo de sus couriers
 *   igual es entregable.
 *
 * Sin cobertura resuelta QUIEN LO CONSUME decide qué significa, y las dos
 * lecturas conviven a propósito y no son contradictorias:
 *
 *  - La creación de UN pedido a mano lo trata como "sin validación de comuna":
 *    el usuario elige la región de un desplegable, así que el dato no se
 *    inventa y un catálogo caído no puede bloquear una carga puntual.
 *  - La carga por EXCEL lo bloquea. Ahí la región no la elige nadie: se deriva
 *    de la comuna de cada fila. Sin el mapa habría que inventarla, que es lo
 *    que se hacía con un "Santiago" fijo — y un pedido con la región
 *    equivocada no se nota al cargarlo, se nota tres pasos después en la
 *    cadena. Decisión del dueño de producto (2026-08-27): preferible que el
 *    archivo no entre.
 *
 * Por eso el resultado no es sólo la cobertura: `reason` dice POR QUÉ no hay
 * cobertura, y los motivos no se arreglan igual. `'no-coverage'` es un estado
 * PERMANENTE (la empresa no tiene contrato activo o su operador no declaró
 * cobertura): `my-coverage` responde 200 con `coverage: []` y reintentar no
 * cambia nada — al comercio hay que decirle que pida la configuración, no que
 * reintente. `'unresolvable'` (comunas cubiertas que el catálogo de regiones no
 * ubica) tampoco suele arreglarse reintentando: hay que revisar la cobertura o
 * el catálogo. `'api-error'` sí puede pasar solo, salvo que el backend explique
 * el motivo (un 401/403 con texto): ese texto viaja en `message` para que el
 * aviso lo muestre en vez de sugerir reintentar.
 *
 * @param {string} countryId País del comercio (de `useCountry()`). Sin él no se
 *   puede resolver la región de cada comuna.
 * @returns {Promise<{coverage: {names: string[], index: Set<string>,
 *   regionByCommune: Map<string,string>,
 *   byRegionCode: Map<string,{full: boolean, communes: Set<string>}> | null,
 *   operators: Array} | null,
 *   reason: 'ok'|'no-coverage'|'unresolvable'|'api-error', message?: string}>}
 *   `coverage` es `null` siempre que `reason` no sea `'ok'`. `message` sólo
 *   aparece con `'api-error'` cuando el cuerpo del error trae texto utilizable.
 */
export async function resolveCoverage(countryId) {
  try {
    const resp = await getMyCoverage();
    const data = resp?.data?.data ?? resp?.data;
    const coverage = Array.isArray(data?.coverage) ? data.coverage : [];
    if (coverage.length === 0) {
      return { coverage: null, reason: "no-coverage" };
    }

    // Una lectura del catálogo por región, en paralelo. Son pocas (las regiones
    // que el operador declara cubrir), no una por comuna.
    const perRegion = await Promise.all(
      coverage.map(async (area) => {
        const regionId = String(area?.regionId ?? "");
        const full = area?.full === true;
        if (!regionId) return { regionId, full, names: [] };

        const catalogResp = await getCommunesByRegion(regionId);
        const catalogBody = catalogResp?.data?.data ?? catalogResp?.data;
        const catalog = Array.isArray(catalogBody?.communes)
          ? catalogBody.communes
          : [];

        if (full) {
          return { regionId, full, names: catalog.map((c) => c?.name).filter(Boolean) };
        }

        const wanted = new Set((area?.communeIds || []).map(String));
        const names = catalog
          .filter((c) => wanted.has(String(c?._id)))
          .map((c) => c?.name)
          .filter(Boolean);
        return { regionId, full, names };
      }),
    );

    const names = [...new Set(perRegion.flatMap((area) => area.names))].sort((a, b) =>
      a.localeCompare(b, "es"),
    );

    // Cobertura declarada pero sin ninguna comuna resoluble: se trata como
    // desconocida en vez de como "no cubre nada", que rechazaría todo el archivo.
    if (names.length === 0) {
      return { coverage: null, reason: "unresolvable" };
    }

    // La región de CADA comuna. Sin este mapa la carga por Excel no puede
    // estampar `shipping_address.city` —que en el schema guarda la REGIÓN, no
    // una ciudad— y tendría que inventar un valor. Es exactamente lo que se
    // hacía antes con un "Santiago" fijo, y dejaba a todos esos pedidos fuera
    // del filtro por región del portal; en Argentina, además, con la región de
    // otro país.
    //
    // Si no se puede armar, no hay cobertura utilizable: quien lo consume
    // decide, y la carga por Excel prefiere no dejar entrar el pedido. Un fallo
    // del endpoint de regiones no llega acá: se lanza y sale como 'api-error'.
    const regionByCommune = await buildRegionByCommune(countryId);
    if (!regionByCommune) {
      return { coverage: null, reason: "unresolvable" };
    }

    // Toda comuna cubierta tiene que poder resolver su región. Si el catálogo
    // de regiones no las cubre a todas, el archivo podría pasar la validación
    // de cobertura y aun así quedarse sin región en algunas filas.
    const sinRegion = names.filter((n) => !regionByCommune.has(normalizeCommune(n)));
    if (sinRegion.length > 0) {
      return { coverage: null, reason: "unresolvable" };
    }

    return {
      coverage: {
        names,
        index: buildCommuneIndex(names),
        regionByCommune,
        byRegionCode: await buildCoverageByRegionCode(countryId, perRegion),
        operators: Array.isArray(data?.operators) ? data.operators : [],
      },
      reason: "ok",
    };
  } catch (err) {
    // Sólo el texto que explica algo (apiErrorMessage descarta 'Unauthorized',
    // 'Network Error' y similares); sin él, el aviso queda en "reintenta".
    const message =
      err instanceof RegionsCatalogError ? apiBodyMessage(err.body) : apiErrorMessage(err);
    return { coverage: null, reason: "api-error", ...(message ? { message } : {}) };
  }
}
